import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NumericFormat as NumberFormat } from "react-number-format";
import ofertasService, { ALCANCE, TIPO_OFERTA } from "../../../_services/cart/ofertas.service";
import alertService from "../../../_services/cart/alert.service";

import CargadorVersiones from "../../../_helpers/CargadorVersiones";
/**
 * El alta y la edición de una oferta.
 *
 * <h3>El alcance es la primera pregunta</h3>
 * Y no una más: cambia todas las demás. Con "publicaciones elegidas" hay que abrir el selector,
 * con "marca y modelo" hay que escribir una regla, y con "baja" alcanza con señalar el auto. Hacer
 * las tres preguntas juntas —que es lo que sale si el formulario es una lista plana de campos—
 * deja al vendedor completando cosas que su oferta no va a usar.
 *
 * <h3>La vista previa del descuento</h3>
 * El bloque de abajo aplica la fórmula sobre un precio de ejemplo mientras se escribe. Existe
 * porque "porcentaje / importe fijo / precio fijo" son tres cosas distintas que se cargan en el
 * mismo campo `valor`, y sin ver el resultado es fácil cargar "5000" pensando en pesos cuando el
 * tipo quedó en porcentaje. La cuenta está duplicada respecto del back a propósito: acá es una
 * ayuda visual, allá es lo que se cobra, y el que manda es el back.
 */

const ALCANCES = [
    {
        key: ALCANCE.PRODUCTOS,
        titulo: "Publicaciones elegidas",
        ayuda: "Vos elegís una por una cuáles entran. Es la oferta clásica de vidriera.",
        icono: "fa-solid fa-hand-pointer",
    },
    {
        key: ALCANCE.MARCA_MODELO,
        titulo: "Marca y modelo",
        ayuda: "Todo lo que cumpla la regla, incluso lo que publiques mañana.",
        icono: "fa-solid fa-car",
    },
    {
        key: ALCANCE.BAJA,
        titulo: "Una baja entera",
        ayuda: "El auto dado de baja y todos los repuestos que cuelgan de él.",
        icono: "fa-solid fa-car-burst",
    },
];

const TIPOS = [
    { key: TIPO_OFERTA.PORCENTAJE, titulo: "Porcentaje", sufijo: "%", ayuda: "Descuento sobre el precio de lista" },
    { key: TIPO_OFERTA.IMPORTE_FIJO, titulo: "Importe fijo", sufijo: "$", ayuda: "Plata que se resta del precio" },
    { key: TIPO_OFERTA.PRECIO_FIJO, titulo: "Precio final", sufijo: "$", ayuda: "El precio al que queda, no lo que se descuenta" },
];

/** Hoy, en formato `yyyy-MM-dd` local. `toISOString()` es UTC y en Argentina adelanta un día. */
const hoyISO = () => {
    const ahora = new Date();
    const mes = String(ahora.getMonth() + 1).padStart(2, "0");
    const dia = String(ahora.getDate()).padStart(2, "0");
    return `${ahora.getFullYear()}-${mes}-${dia}`;
};

const vacio = () => ({
    nombre: "",
    descripcion: "",
    condiciones: "",
    alcance: ALCANCE.PRODUCTOS,
    marcaId: null,
    modeloId: null,
    versionId: null,
    tipoRepuestoId: null,
    productoBajaId: null,
    tipo: TIPO_OFERTA.PORCENTAJE,
    valor: "",
    topeDescuento: "",
    desde: hoyISO(),
    hasta: "",
    activa: true,
});

/** La misma fórmula que aplica el back, para la vista previa. Ver el comentario de arriba. */
const simular = (base, tipo, valor, tope) => {
    const v = Number(valor);
    if (!v || v <= 0) return null;

    let fin;
    if (tipo === TIPO_OFERTA.PORCENTAJE) fin = base - (base * v) / 100;
    else if (tipo === TIPO_OFERTA.IMPORTE_FIJO) fin = base - v;
    else fin = v;

    const techo = Number(tope);
    if (techo > 0 && fin < base - techo) fin = base - techo;

    return Math.max(0, Math.round(fin));
};

const PRECIO_EJEMPLO = 100000;

export const OfertaForm = ({ ofertaId, filtros, onClose, onGuardada }) => {
    const [valores, setValores] = useState(vacio);
    const [bajas, setBajas] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [cargando, setCargando] = useState(!!ofertaId);

    useEffect(() => {
        ofertasService
            .bajas()
            .then((resp) => setBajas(resp.data || []))
            .catch(() => setBajas([]));
    }, []);

    useEffect(() => {
        if (!ofertaId) {
            setValores(vacio());
            return;
        }

        setCargando(true);

        ofertasService
            .detalle(ofertaId)
            .then((resp) => {
                const o = resp.data;
                setValores({
                    nombre: o.nombre || "",
                    descripcion: o.descripcion || "",
                    condiciones: o.condiciones || "",
                    alcance: o.alcance,
                    marcaId: o.marca?.id ?? null,
                    modeloId: o.modelo?.id ?? null,
                    versionId: o.version?.id ?? null,
                    tipoRepuestoId: o.tipoRepuesto?.id ?? null,
                    productoBajaId: o.productoBaja?.id ?? null,
                    tipo: o.tipo,
                    valor: o.valor ?? "",
                    topeDescuento: o.topeDescuento ?? "",
                    desde: o.desde || hoyISO(),
                    hasta: o.hasta || "",
                    activa: o.activa !== false,
                });
            })
            .catch((error) => {
                alertService.error(error);
                onClose();
            })
            .finally(() => setCargando(false));
    }, [ofertaId]); // eslint-disable-line react-hooks/exhaustive-deps

    const set = (campo, valor) => setValores((v) => ({ ...v, [campo]: valor }));

    /*
     * Los modelos y las versiones se filtran por lo que ya se eligió arriba: el combo de modelos trae
     * los del sitio entero (son miles), y sin acotarlo por marca elegir "Focus" es imposible.
     */
    const modelos = useMemo(
        () => (filtros?.modelos || []).filter((m) => !valores.marcaId || m.marca?.id === valores.marcaId),
        [filtros, valores.marcaId]
    );

    const versiones = useMemo(
        () => (filtros?.versiones || []).filter((v) => !valores.modeloId || v.modelo?.id === valores.modeloId),
        [filtros, valores.modeloId]
    );

    const previa = simular(PRECIO_EJEMPLO, valores.tipo, valores.valor, valores.topeDescuento);

    const guardar = () => {
        if (!valores.nombre.trim()) {
            alertService.error("Poné un nombre para la oferta");
            return;
        }

        setGuardando(true);

        const payload = {
            ...valores,
            valor: Number(valores.valor),
            topeDescuento: valores.topeDescuento === "" ? null : Number(valores.topeDescuento),
            hasta: valores.hasta || null,
        };

        const promesa = ofertaId
            ? ofertasService.actualizar(ofertaId, payload)
            : ofertasService.crear(payload);

        promesa
            .then((resp) => {
                alertService.msg(ofertaId ? "Oferta actualizada" : "Oferta creada", "success");
                onGuardada(ofertaId || resp?.data?.id, valores.alcance);
            })
            .catch((error) => alertService.error(error))
            .finally(() => setGuardando(false));
    };

    const selectProps = {
        className: "ofertas__select",
        classNamePrefix: "ofertas__select",
        isClearable: true,
        getOptionLabel: (o) => o.nombre,
        getOptionValue: (o) => o.id,
        menuPortalTarget: document.body,
        styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
    };

    return (
        <Modal show onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>{ofertaId ? "Editar oferta" : "Nueva oferta"}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {cargando ? (
                    <p className="panel__estado-carga">Cargando…</p>
                ) : (
                    <div className="ofertaForm">
                        <label className="ofertaForm__campo">
                            <span>Nombre</span>
                            <input
                                type="text"
                                maxLength={120}
                                value={valores.nombre}
                                placeholder="Liquidación Focus 2012"
                                onChange={(e) => set("nombre", e.target.value)}
                            />
                            <small>Así la va a ver el comprador en la publicación.</small>
                        </label>

                        {/* El alcance primero: es lo que decide qué otras preguntas aparecen abajo. */}
                        <fieldset className="ofertaForm__grupo">
                            <legend>¿Sobre qué corre?</legend>

                            <div className="ofertaForm__opciones">
                                {ALCANCES.map((a) => (
                                    <button
                                        key={a.key}
                                        type="button"
                                        className={
                                            valores.alcance === a.key
                                                ? "ofertaForm__opcion ofertaForm__opcion--activa"
                                                : "ofertaForm__opcion"
                                        }
                                        onClick={() => set("alcance", a.key)}
                                    >
                                        <FontAwesomeIcon icon={a.icono} />
                                        <strong>{a.titulo}</strong>
                                        <small>{a.ayuda}</small>
                                    </button>
                                ))}
                            </div>

                            {valores.alcance === ALCANCE.PRODUCTOS && (
                                <p className="ofertaForm__nota">
                                    <FontAwesomeIcon icon="fa-solid fa-circle-info" />
                                    Después de guardar vas a poder elegir las publicaciones desde el botón
                                    “Publicaciones” del listado.
                                </p>
                            )}

                            {valores.alcance === ALCANCE.MARCA_MODELO && (
                                <>
                                    <div className="ofertaForm__fila">
                                        <label className="ofertaForm__campo">
                                            <span>Marca</span>
                                            <Select
                                                {...selectProps}
                                                placeholder="Cualquiera"
                                                options={filtros?.marcas || []}
                                                value={(filtros?.marcas || []).find((m) => m.id === valores.marcaId) || null}
                                                onChange={(opt) =>
                                                    setValores((v) => ({
                                                        ...v,
                                                        marcaId: opt?.id ?? null,
                                                        // Cambiar la marca invalida el modelo y la versión que
                                                        // estaban elegidos: si no se limpian, queda una regla
                                                        // imposible que no alcanza a ninguna publicación.
                                                        modeloId: null,
                                                        versionId: null,
                                                    }))
                                                }
                                            />
                                        </label>

                                        <label className="ofertaForm__campo">
                                            <span>Modelo</span>
                                            <Select
                                                {...selectProps}
                                                placeholder="Cualquiera"
                                                options={modelos}
                                                isDisabled={!valores.marcaId}
                                                value={modelos.find((m) => m.id === valores.modeloId) || null}
                                                onChange={(opt) =>
                                                    setValores((v) => ({
                                                        ...v,
                                                        modeloId: opt?.id ?? null,
                                                        versionId: null,
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <CargadorVersiones modeloId={valores.modeloId}/>

                                    <div className="ofertaForm__fila">
                                        <label className="ofertaForm__campo">
                                            <span>Versión</span>
                                            <Select
                                                {...selectProps}
                                                placeholder="Cualquiera"
                                                options={versiones}
                                                isDisabled={!valores.modeloId}
                                                value={versiones.find((v) => v.id === valores.versionId) || null}
                                                onChange={(opt) => set("versionId", opt?.id ?? null)}
                                            />
                                        </label>

                                        <label className="ofertaForm__campo">
                                            <span>Tipo de repuesto</span>
                                            <Select
                                                {...selectProps}
                                                placeholder="Cualquiera"
                                                options={filtros?.tipoRepuestos || []}
                                                value={
                                                    (filtros?.tipoRepuestos || []).find(
                                                        (t) => t.id === valores.tipoRepuestoId
                                                    ) || null
                                                }
                                                onChange={(opt) => set("tipoRepuestoId", opt?.id ?? null)}
                                            />
                                        </label>
                                    </div>

                                    <p className="ofertaForm__nota">
                                        <FontAwesomeIcon icon="fa-solid fa-circle-info" />
                                        Los criterios que cargues se suman entre sí: “Ford” + “Focus” alcanza los
                                        Focus, no todos los Ford. Dejá en blanco lo que no quieras filtrar.
                                    </p>
                                </>
                            )}

                            {valores.alcance === ALCANCE.BAJA && (
                                <label className="ofertaForm__campo">
                                    <span>Baja</span>
                                    <Select
                                        {...selectProps}
                                        placeholder="Elegí el auto"
                                        options={bajas}
                                        getOptionLabel={(o) =>
                                            [o.marca?.nombre, o.modelo?.nombre, o.anio, o.descripcion]
                                                .filter(Boolean)
                                                .join(" ")
                                                .slice(0, 80)
                                        }
                                        value={bajas.find((b) => b.id === valores.productoBajaId) || null}
                                        onChange={(opt) => set("productoBajaId", opt?.id ?? null)}
                                    />
                                    <small>
                                        Alcanza a todos los repuestos de esa baja, incluso los que publiques después.
                                    </small>
                                </label>
                            )}
                        </fieldset>

                        <fieldset className="ofertaForm__grupo">
                            <legend>¿Cuánto descuenta?</legend>

                            <div className="ofertaForm__opciones ofertaForm__opciones--chicas">
                                {TIPOS.map((t) => (
                                    <button
                                        key={t.key}
                                        type="button"
                                        className={
                                            valores.tipo === t.key
                                                ? "ofertaForm__opcion ofertaForm__opcion--activa"
                                                : "ofertaForm__opcion"
                                        }
                                        onClick={() => set("tipo", t.key)}
                                    >
                                        <strong>{t.titulo}</strong>
                                        <small>{t.ayuda}</small>
                                    </button>
                                ))}
                            </div>

                            <div className="ofertaForm__fila">
                                <label className="ofertaForm__campo">
                                    <span>
                                        Valor {valores.tipo === TIPO_OFERTA.PORCENTAJE ? "(%)" : "($)"}
                                    </span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={valores.valor}
                                        onChange={(e) => set("valor", e.target.value)}
                                    />
                                </label>

                                {/* El tope sólo tiene sentido con porcentaje: con importe o precio fijo
                                    el descuento ya está acotado por definición. */}
                                {valores.tipo === TIPO_OFERTA.PORCENTAJE && (
                                    <label className="ofertaForm__campo">
                                        <span>Tope de descuento ($)</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            placeholder="Sin tope"
                                            value={valores.topeDescuento}
                                            onChange={(e) => set("topeDescuento", e.target.value)}
                                        />
                                        <small>
                                            El mismo 15% son $600 en una bujía y $300.000 en un motor. Con tope, nunca
                                            se descuenta más que esto.
                                        </small>
                                    </label>
                                )}
                            </div>

                            {previa !== null && (
                                <p className="ofertaForm__previa">
                                    Sobre un repuesto de <NumberFormat prefix="$" value={PRECIO_EJEMPLO} displayType="text" thousandSeparator="." decimalSeparator="," />{" "}
                                    quedaría en{" "}
                                    <strong>
                                        <NumberFormat prefix="$" value={previa} displayType="text" thousandSeparator="." decimalSeparator="," />
                                    </strong>{" "}
                                    ({Math.round(((PRECIO_EJEMPLO - previa) / PRECIO_EJEMPLO) * 100)}% off)
                                </p>
                            )}
                        </fieldset>

                        <fieldset className="ofertaForm__grupo">
                            <legend>¿Cuándo corre?</legend>

                            <div className="ofertaForm__fila">
                                <label className="ofertaForm__campo">
                                    <span>Desde</span>
                                    <input
                                        type="date"
                                        value={valores.desde}
                                        onChange={(e) => set("desde", e.target.value)}
                                    />
                                </label>

                                <label className="ofertaForm__campo">
                                    <span>Hasta</span>
                                    <input
                                        type="date"
                                        value={valores.hasta}
                                        min={valores.desde}
                                        onChange={(e) => set("hasta", e.target.value)}
                                    />
                                    <small>Vacío = sin fecha de fin.</small>
                                </label>
                            </div>

                            {/*
                                El interruptor va junto a las fechas y no arriba con el nombre: es la otra mitad
                                de "cuándo corre". Una oferta puede estar dentro de su vigencia y apagada —así se
                                pausa sin perder el rango de fechas original.
                            */}
                            <label className="ofertaForm__check">
                                <input
                                    type="checkbox"
                                    checked={valores.activa}
                                    onChange={(e) => set("activa", e.target.checked)}
                                />
                                <span>
                                    Activa
                                    <small>Si la apagás, deja de aplicarse pero no se pierde.</small>
                                </span>
                            </label>
                        </fieldset>

                        <fieldset className="ofertaForm__grupo">
                            <legend>Texto para el comprador</legend>

                            <label className="ofertaForm__campo">
                                <span>Frase corta</span>
                                <input
                                    type="text"
                                    maxLength={300}
                                    placeholder="Hasta agotar stock"
                                    value={valores.descripcion}
                                    onChange={(e) => set("descripcion", e.target.value)}
                                />
                                <small>Acompaña al precio en la publicación.</small>
                            </label>

                            <label className="ofertaForm__campo">
                                <span>Condiciones</span>
                                <textarea
                                    rows={3}
                                    maxLength={1000}
                                    placeholder="No incluye envío. No acumulable con otras promociones."
                                    value={valores.condiciones}
                                    onChange={(e) => set("condiciones", e.target.value)}
                                />
                                <small>La letra chica. Se muestra en la ficha y en el carrito.</small>
                            </label>
                        </fieldset>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button type="button" className="button" data-type="secondary" onClick={onClose}>
                    Cancelar
                </button>
                <button
                    type="button"
                    className="button"
                    data-type="primary"
                    disabled={guardando || cargando}
                    onClick={guardar}
                >
                    {guardando ? "Guardando…" : "Guardar"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};
